import { CloseIcon } from '@chakra-ui/icons';
import { deburr } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { ExitIcon, NoCompany } from '@revelio/assets';
import type {
  ColumnMappings,
  Subsidiary,
  SubsidiaryMappingKeys,
} from '@revelio/core';

import { MapperModal } from '../../deliverables/company-selection/subsidiary-mapping/mapper/mapper-modal';
import {
  fetchSubsidiaries,
  formatSubsidiaries,
} from '../../deliverables/company-selection/subsidiary-mapping/subsidiary-mapping.api';
import modalStyles from '../data-builder-modals.module.css';

const isMatchingTitle = (title: string, searchString: string) => {
  const sanitizedTitle = deburr(title.toLowerCase());
  const sanitizedSearchString = deburr(searchString.toLowerCase().trim());

  return sanitizedTitle.includes(sanitizedSearchString);
};

export interface DataBuilderSubsidiaryMappingProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  initialMappings: ColumnMappings;
  onSubmit: (mappings: ColumnMappings) => void;
  submitIsLoading: boolean;
}

const DEFAULT_SELECTED_COLUMNS: SubsidiaryMappingKeys[] = ['primary'];

export function DataBuilderSubsidiaryMapping({
  isOpen,
  onClose,
  onBack,
  initialMappings,
  onSubmit,
  submitIsLoading,
}: DataBuilderSubsidiaryMappingProps) {
  const [mappings, setMappings] = useState<ColumnMappings>(initialMappings);
  const [selectedColumns, setSelectedColumns] = useState<
    SubsidiaryMappingKeys[]
  >(DEFAULT_SELECTED_COLUMNS);
  const [addItemToColumn, setAddItemToColumn] = useState<
    [] | [keyof ColumnMappings, Subsidiary]
  >([]);
  const [selectedColumn, setSelectedColumn] = useState<string | number>('');

  // Reset state when initialMappings change (new selection)
  useEffect(() => {
    setMappings(initialMappings);
    setSelectedColumns(DEFAULT_SELECTED_COLUMNS);
  }, [initialMappings]);

  // Column cleanup: remove orphaned columns when mappings change
  useEffect(() => {
    setSelectedColumns((prevData) => {
      let updatedColumns = [...prevData];
      for (const [index, col] of prevData.entries()) {
        if (index === 0) continue;
        const prevColId = prevData[index - 1];
        if (prevColId === undefined) {
          updatedColumns = [...prevData.slice(0, index)];
          break;
        }
        const foundColumnAsSubsidiary = mappings[prevColId]?.subsidiaries?.find(
          (sub) => sub.id === col
        );
        if (!foundColumnAsSubsidiary) {
          updatedColumns = [...prevData.slice(0, index)];
          break;
        }
      }
      return updatedColumns;
    });
  }, [mappings]);

  // Deferred add-to-column: waits for selectedColumns to update before adding
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (addItemToColumn.length) {
      const [column, item] = addItemToColumn;
      onAddToColumn(column)(item);
      setAddItemToColumn([]);
    }
  }, [selectedColumns, addItemToColumn]);

  const setColumnLoading = (
    company: Subsidiary,
    isLoading: boolean,
    subsidiaries: Subsidiary[] = []
  ) => {
    setMappings((prev) => ({
      ...prev,
      [company.id]: {
        name: `${company.title}`,
        title: `${company.title} Subsidiaries`,
        selectAll: false,
        subsidiaries,
        isLoading,
      },
    }));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  const onCompanyClick = useCallback(
    async (company: Subsidiary, index: number) => {
      if (selectedColumns[index + 1]) {
        setSelectedColumns((prev) => [...prev.slice(0, index + 1), company.id]);
      } else {
        setSelectedColumns((prev) => [...prev, company.id]);
      }

      if (!mappings[company.id]) {
        setColumnLoading(company, true);
        const companySubsidiaries = await fetchSubsidiaries(company.rcid);
        setColumnLoading(company, false, companySubsidiaries);
      }
    },
    [mappings, selectedColumns]
  );

  const updateHeadcounts = useCallback(
    (
      companyIdToUpdate: keyof ColumnMappings,
      numberToUpdateBy: number,
      mapping: ColumnMappings,
      onlyOnce?: boolean
    ): ColumnMappings => {
      const foundIndex = selectedColumns.indexOf(companyIdToUpdate);
      if (foundIndex <= 0) return mapping;

      const prevCompanyId = selectedColumns[foundIndex - 1];
      if (prevCompanyId === undefined) return mapping;

      const nextMapping = onlyOnce
        ? mapping
        : updateHeadcounts(prevCompanyId, numberToUpdateBy, mapping);
      const prevCompany = nextMapping[prevCompanyId];
      if (prevCompany === undefined) return nextMapping;

      return {
        ...nextMapping,
        [prevCompanyId]: {
          ...prevCompany,
          subsidiaries: prevCompany.subsidiaries.map((subsidiary) => {
            if (subsidiary.id !== companyIdToUpdate) return subsidiary;
            return {
              ...subsidiary,
              headCount: subsidiary.headCount + numberToUpdateBy,
            };
          }),
        },
      };
    },
    [selectedColumns]
  );

  const onAddToColumn = useCallback(
    (column: keyof ColumnMappings) => (item: Subsidiary) => {
      setMappings((prev) => {
        let updatedMapping: ColumnMappings = { ...prev };
        const prevColumnIndex = selectedColumns.indexOf(column) - 1;

        if (
          !updatedMapping[column]?.subsidiaries?.find(
            (i) => i.id === item.id
          ) &&
          column !== item.id
        ) {
          // Update hasSubsidiaries of a selected company in a previous column
          if (prevColumnIndex > -1) {
            const prevColId = selectedColumns[prevColumnIndex];
            const prevCol =
              prevColId !== undefined ? updatedMapping[prevColId] : undefined;
            if (prevColId !== undefined && prevCol !== undefined) {
              updatedMapping = {
                ...updatedMapping,
                [prevColId]: {
                  ...prevCol,
                  subsidiaries: prevCol.subsidiaries.map((sub) =>
                    sub.id === column ? { ...sub, hasSubsidiaries: true } : sub
                  ),
                },
              };
            }
          }

          const columnData = updatedMapping[column];
          if (columnData !== undefined) {
            updatedMapping = {
              ...updatedMapping,
              [column]: {
                ...columnData,
                subsidiaries: [...columnData.subsidiaries, item],
              },
            };
          }

          // Recursively update headcounts
          updatedMapping = updateHeadcounts(
            column,
            item.headCount,
            updatedMapping
          );
        }
        return updatedMapping;
      });
    },
    [updateHeadcounts, selectedColumns]
  );

  const onAddToItem = useCallback(
    (index: number) => async (item: Subsidiary, columnItemId: string) => {
      const [colId, itemId] = columnItemId.split('__');
      if (!colId || !itemId) return;
      const itemToAddTo = mappings[colId]?.subsidiaries?.find(
        (sub) => sub.id === itemId
      );
      if (itemToAddTo) {
        await onCompanyClick(itemToAddTo, index);
        setAddItemToColumn([itemToAddTo.id, item]);
      }
    },
    [onCompanyClick, mappings]
  );

  const verifyAddToItem = (item: Subsidiary, columnItemId: string) => {
    const [colId, itemId] = columnItemId.split('__');
    if (!colId || !itemId) return false;
    const itemToAddTo = mappings[colId]?.subsidiaries?.find(
      (sub) => sub.id === itemId
    );
    if (itemToAddTo) {
      let previewSelectedColumns = [...selectedColumns];
      const indexOfColumn = previewSelectedColumns.indexOf(colId);
      if (selectedColumns[indexOfColumn + 1]) {
        previewSelectedColumns = [
          ...previewSelectedColumns.slice(0, indexOfColumn + 1),
          itemToAddTo.id,
        ];
      } else {
        previewSelectedColumns = [...previewSelectedColumns, itemToAddTo.id];
      }
      const indexOfItem = previewSelectedColumns.indexOf(item.id);
      const isAlreadySubsidiary = mappings[itemToAddTo.id]?.subsidiaries?.find(
        (i) => i.id === item.id
      );
      const isSameColumn = itemToAddTo.id === item.id;
      return (
        !isAlreadySubsidiary &&
        !isSameColumn &&
        (indexOfItem < 0 || indexOfColumn < indexOfItem)
      );
    }
    return false;
  };

  const verifyAddToColumn = useCallback(
    (column: keyof ColumnMappings) => (item: Subsidiary) => {
      const indexOfItem = selectedColumns.indexOf(item.id);
      const indexOfColumn = selectedColumns.indexOf(column);
      const isAlreadySubsidiary = mappings[column]?.subsidiaries?.find(
        (i) => i.id === item.id
      );
      const isSameColumn = column === item.id;
      return (
        !isAlreadySubsidiary &&
        !isSameColumn &&
        (indexOfItem < 0 || indexOfColumn < indexOfItem)
      );
    },
    [mappings, selectedColumns]
  );

  const onMoveSubsidiaryBack = useCallback(
    (
      fromColumn: keyof ColumnMappings,
      toColumn: keyof ColumnMappings,
      item: Subsidiary
    ) => {
      setMappings((prev) => {
        let updatedMapping: ColumnMappings = { ...prev };
        if (
          !updatedMapping[toColumn]?.subsidiaries?.find(
            (i) => i.id === item.id
          ) &&
          toColumn !== item.id
        ) {
          const updatedFilteredSubsidiaries =
            updatedMapping[fromColumn]?.subsidiaries.filter(
              (s) => s.id !== item.id
            ) || [];

          const toColumnData = updatedMapping[toColumn];
          if (
            !updatedFilteredSubsidiaries.length &&
            toColumnData !== undefined
          ) {
            updatedMapping = {
              ...updatedMapping,
              [toColumn]: {
                ...toColumnData,
                subsidiaries: toColumnData.subsidiaries.map((sub) =>
                  sub.id === fromColumn
                    ? { ...sub, hasSubsidiaries: false }
                    : sub
                ),
              },
            };
          }

          const fromColumnData = updatedMapping[fromColumn];
          const toColumnDataAfter = updatedMapping[toColumn];
          if (fromColumnData !== undefined && toColumnDataAfter !== undefined) {
            updatedMapping = {
              ...updatedMapping,
              [fromColumn]: {
                ...fromColumnData,
                subsidiaries: [...updatedFilteredSubsidiaries],
              },
              [toColumn]: {
                ...toColumnDataAfter,
                subsidiaries: [...toColumnDataAfter.subsidiaries, item],
              },
            };
          }

          updatedMapping = updateHeadcounts(
            fromColumn,
            -1 * item.headCount,
            updatedMapping,
            true
          );
        }
        return updatedMapping;
      });
    },
    [updateHeadcounts]
  );

  const onDeleteCompany = useCallback(
    (column: keyof ColumnMappings, item: Subsidiary) => {
      setMappings((prev) => {
        let updatedMapping = { ...prev };
        const columnDataForDelete = updatedMapping[column];
        if (columnDataForDelete === undefined) return prev;
        const updatedSubsidiaries = columnDataForDelete.subsidiaries.filter(
          (s) => s.id !== item.id
        );
        const prevColumnIndex = selectedColumns.indexOf(column) - 1;

        updatedMapping = {
          ...updatedMapping,
          [column]: {
            ...columnDataForDelete,
            subsidiaries: [...updatedSubsidiaries],
          },
        };

        if (!updatedSubsidiaries.length && prevColumnIndex > -1) {
          const prevColId = selectedColumns[prevColumnIndex];
          const prevColForDelete =
            prevColId !== undefined ? updatedMapping[prevColId] : undefined;
          if (prevColId !== undefined && prevColForDelete !== undefined) {
            updatedMapping = {
              ...updatedMapping,
              [prevColId]: {
                ...prevColForDelete,
                subsidiaries: prevColForDelete.subsidiaries.map((sub) =>
                  sub.id === column ? { ...sub, hasSubsidiaries: false } : sub
                ),
              },
            };
          }
        }

        updatedMapping = updateHeadcounts(
          column,
          -1 * item.headCount,
          updatedMapping
        );

        return updatedMapping;
      });
    },
    [selectedColumns, updateHeadcounts]
  );

  const getMenuOptions = useCallback(
    (index: number) => (item: Subsidiary) => {
      const colId = selectedColumns[index];
      const options = [
        {
          id: 'delete-item',
          title: 'Delete Company',
          icon: (
            <CloseIcon
              className={modalStyles.modalInkText}
              color="navyBlue.500"
            />
          ),
          onClick: () => {
            if (colId !== undefined) onDeleteCompany(colId, item);
          },
        },
      ];
      if (index !== 0) {
        const prevColId = selectedColumns[index - 1];
        options.unshift({
          id: 'make-parent',
          title: 'Make Parent',
          icon: <ExitIcon width={3.5} height={4} />,
          onClick: () => {
            if (colId !== undefined && prevColId !== undefined) {
              onMoveSubsidiaryBack(colId, prevColId, item);
            }
          },
        });
      }
      return options;
    },
    [selectedColumns, onMoveSubsidiaryBack, onDeleteCompany]
  );

  const toggleSubsidiariesSelections = (
    companyId: keyof ColumnMappings,
    toggle: boolean | undefined,
    mapping: ColumnMappings
  ): ColumnMappings => {
    const companyData = mapping[companyId];
    if (!companyData?.subsidiaries) return { ...mapping };

    let result = { ...mapping };

    const updatedSubsidiaries = companyData.subsidiaries.map((subsidiary) => {
      if (result[subsidiary.id]?.subsidiaries) {
        result = toggleSubsidiariesSelections(subsidiary.id, toggle, result);
      }

      return {
        ...subsidiary,
        isSelected: toggle,
        isIndeterminate: false,
      };
    });

    result[companyId] = {
      ...companyData,
      subsidiaries: updatedSubsidiaries,
    };

    return result;
  };

  const toggleParentItems = (
    column: string | number,
    updatedMapping: ColumnMappings
  ) => {
    if (column === 'primary') return;

    const columnIndex = selectedColumns.indexOf(column);
    const parentColumnId = selectedColumns[columnIndex - 1];
    if (parentColumnId === undefined || !updatedMapping[parentColumnId]) return;

    const parentItemIndex = updatedMapping[
      parentColumnId
    ].subsidiaries.findIndex((subItem) => subItem.id === column);

    const parentItemExists = parentItemIndex !== -1;
    const parentItem = updatedMapping[parentColumnId];
    let parentSubsidiaries = parentItem?.subsidiaries;

    if (parentSubsidiaries && parentItemExists) {
      const totalSubsidiaries = updatedMapping[column]?.subsidiaries?.length;
      const { numSelected, numIndeterminate } = (
        updatedMapping[column]?.subsidiaries ?? []
      ).reduce(
        (total, currentSubsidiary) => {
          const selectedDelta = +(currentSubsidiary.isSelected || false);
          const indeterminateDelta = +(
            currentSubsidiary.isIndeterminate || false
          );
          return {
            numSelected: total.numSelected + selectedDelta,
            numIndeterminate: total.numIndeterminate + indeterminateDelta,
          };
        },
        { numSelected: 0, numIndeterminate: 0 }
      );

      const isIndeterminate =
        (numSelected > 0 || numIndeterminate > 0) &&
        numSelected < (totalSubsidiaries ?? 0);

      parentSubsidiaries = parentSubsidiaries.map((sub, idx) =>
        idx === parentItemIndex ? { ...sub, isIndeterminate } : sub
      );

      updatedMapping[parentColumnId] = {
        ...updatedMapping[parentColumnId],
        subsidiaries: parentSubsidiaries,
      };

      toggleParentItems(parentColumnId, updatedMapping);
    }
  };

  const toggleSelectAllInColumn = (
    column: keyof ColumnMappings,
    toggle: boolean,
    searchString = ''
  ) => {
    setMappings((prev) => {
      let updatedMapping = { ...prev };
      const columnData = updatedMapping[column];
      if (!columnData) return prev;

      const updatedColumnSubsidiaries = columnData.subsidiaries.map(
        (subsidiary) => {
          const isMatchedSubsidiary =
            !searchString || isMatchingTitle(subsidiary.title, searchString);

          if (!isMatchedSubsidiary) return subsidiary;

          updatedMapping = toggleSubsidiariesSelections(
            subsidiary.id,
            toggle,
            updatedMapping
          );
          return { ...subsidiary, isSelected: toggle };
        }
      );

      const columnDataToggle = updatedMapping[column];
      if (columnDataToggle !== undefined) {
        updatedMapping[column] = {
          ...columnDataToggle,
          selectAll: toggle,
          subsidiaries: updatedColumnSubsidiaries,
        };
      }

      toggleParentItems(column, updatedMapping);

      return updatedMapping;
    });
  };

  const toggleSelected =
    (column: keyof ColumnMappings) => (itemId: string | number) => {
      setMappings((prev) => {
        let updatedMapping = { ...prev };
        const columnData = updatedMapping[column];
        if (!columnData) return prev;
        let toggle: boolean | undefined;

        const updatedSubsidiaries = columnData.subsidiaries.map(
          (subsidiary) => {
            if (subsidiary.id === itemId) {
              toggle = !subsidiary.isSelected;
              return {
                ...subsidiary,
                isSelected: !subsidiary.isSelected,
                isIndeterminate: false,
              };
            }
            return subsidiary;
          }
        );

        const columnDataItem = updatedMapping[column];
        if (columnDataItem !== undefined) {
          updatedMapping = {
            ...updatedMapping,
            [column]: {
              ...columnDataItem,
              subsidiaries: updatedSubsidiaries,
            },
          };
        }

        updatedMapping = toggleSubsidiariesSelections(
          itemId,
          toggle,
          updatedMapping
        );

        toggleParentItems(column, updatedMapping);

        return updatedMapping;
      });
    };

  const handleSubmit = () => {
    onSubmit(mappings);
  };

  return (
    <MapperModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitIsLoading={submitIsLoading}
      header={submitIsLoading ? 'Adding Subsidiaries...' : 'Edit Companies'}
      selectedColumns={selectedColumns}
      mappings={mappings}
      setSelectedColumn={setSelectedColumn}
      onAddToColumn={onAddToColumn}
      verifyAddToColumn={verifyAddToColumn}
      onAddToItem={onAddToItem}
      verifyAddToItem={verifyAddToItem}
      onItemClick={onCompanyClick}
      toggleItemSelected={toggleSelected}
      getMenuOptions={getMenuOptions}
      toggleSelectAllInColumn={toggleSelectAllInColumn}
      emptyListProps={{
        addText: 'Add a subsidiary',
        body: (data) =>
          `${
            data?.name || ''
          } has no subsidiaries but you can add one at your own risk`,
        header: 'No subsidiaries',
        icon: <NoCompany />,
      }}
      returnToPrevious={onBack}
      backText="Select Companies"
      entitySelectionsModalProps={{
        header: 'Add Subsidiary',
        submitText: 'Add Subsidiary',
        treeHeight: 310,
        showPlaceholders: true,
        submitHandler: (selections: Subsidiary[]) => {
          const addFunction = onAddToColumn(selectedColumn);
          selections.forEach((sub) => {
            const subsidiaryToAdd = formatSubsidiaries(
              sub as unknown as Parameters<typeof formatSubsidiaries>[0]
            );
            addFunction(subsidiaryToAdd);
          });
        },
      }}
    />
  );
}
